import {
  ErrorOutlineOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  MessageTooltip,
  Spacer,
  TableTagHomeChargingConnectionStatus,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { formatDate, getDate } from "../../../libSlice/materialUiFormats";
import {
  getElectricVehiclesByCompanyAsync,
  getHomeChargingEligibleVehiclesAsync,
  selectElectricVehicles,
  selectElectricVehiclesStatus,
  selectHomeChargingEligibleVehiclesStatus,
} from "./electricVehiclesSlice";
import { selectManager } from "../homeSlice";
import {
  ElectricVehicleApi,
  ElectricVehicleDisplayed,
} from "./electricVehiclesAPI";
import { ElectricVehicleSidePanel } from "./sidePanel/electricVehicleSidePanel/SidePanel";
import "../../../i18n/i18n";

export const ElectricVehiclesTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const firstPath = location.pathname.split("/")[1];
  const electricVehicles = useAppSelector(selectElectricVehicles);
  const electricVehiclesStatus = useAppSelector(selectElectricVehiclesStatus);
  const homeChargingEligibleVehiclesStatus = useAppSelector(
    selectHomeChargingEligibleVehiclesStatus,
  );
  const manager = useAppSelector(selectManager);
  const isOwnerOrAdmin = useMemo(
    () => manager?.role === "OWNER" || manager?.role === "ADMIN",
    [manager],
  );
  const [listDisplayed, setListDisplayed] = useState<
    ElectricVehicleDisplayed[] | null
  >(null);

  const pageName = "electric_vehicles";
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    "customers-grid",
    pageName,
  );

  useEffect(() => {
    if (electricVehiclesStatus === "idle") {
      void dispatch(getElectricVehiclesByCompanyAsync());
    }
  }, [dispatch, electricVehiclesStatus]);

  useEffect(() => {
    if (isOwnerOrAdmin && homeChargingEligibleVehiclesStatus === "idle") {
      void dispatch(getHomeChargingEligibleVehiclesAsync());
    }
  }, [dispatch, isOwnerOrAdmin, homeChargingEligibleVehiclesStatus]);

  const findKeysToTranslate = useCallback(
    (key: keyof ElectricVehicleDisplayed, value: string) => {
      switch (key) {
        case "status":
          return t(`home_charging.connectionStatus.${value}`);
        case "createdAt":
          return formatDate(new Date(value));
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status", "createdAt"] as (keyof ElectricVehicleDisplayed)[],
    [],
  );

  const allElectricVehiclesDisplayed: (ElectricVehicleApi & { id: string })[] =
    useMemo(() => {
      let rowsWithId = electricVehicles.map((electricVehicle) => {
        const row: ElectricVehicleApi & { id: string } = {
          ...electricVehicle,
          id: electricVehicle.uuid,
        };
        return row;
      });

      keysToTranslate.forEach((key) => {
        rowsWithId = rowsWithId.map((row) => {
          return {
            ...row,
            [key]: findKeysToTranslate(key, row[key]),
          };
        });
      });

      return rowsWithId;
    }, [electricVehicles, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (electricVehiclesStatus === "success") {
      setListDisplayed(allElectricVehiclesDisplayed);
    }
  }, [allElectricVehiclesDisplayed, electricVehiclesStatus]);

  const searchBarProps = {
    values: allElectricVehiclesDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const columnsDisplayed: {
    [index in keyof ElectricVehicleDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      registrationNumber: {
        type: "string",
        width: 250,
        valueFormatter: (value: string) => value || "-",
      },
      status: {
        type: "string",
        width: 250,
        renderCell: (p) => (
          <StyledRow>
            <TableTagHomeChargingConnectionStatus status={p.value as string} />
          </StyledRow>
        ),
      },
      createdAt: {
        type: "date",
        width: 250,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
    };
  }, []);

  const openSidePanelAction = useCallback(
    (row: Partial<ElectricVehicleDisplayed> & { id?: string }) => {
      navigate(`/${firstPath}/electric_charging/electric_vehicles/${row.id}`);
    },
    [navigate, firstPath],
  );

  if (params.tab !== "electric_vehicles") {
    return null;
  }

  return (
    <StyledContainer>
      <MessageTooltip
        type={"NEUTRAL"}
        iconLeft={<ErrorOutlineOutlined />}
        text={<Trans i18nKey="electric_charging.electric_vehicles.banner" />}
      />
      <Spacer y={2} />
      {!listDisplayed ? (
        <LaunchPage hasBorderRadius={true} />
      ) : (
        <MaterialUiTable<
          keyof ElectricVehicleDisplayed,
          ElectricVehicleDisplayed
        >
          rows={listDisplayed}
          columns={columnsDisplayed}
          pageName={pageName}
          dataGridState={dataGridState}
          searchBar={searchBarProps}
          apiRef={apiRef}
          onRowClickAction={openSidePanelAction}
        />
      )}
      <ElectricVehicleSidePanel />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
